// InsideTheCollection.jsx
// ─────────────────────────────────────────────────────────────────────────────
// The single most important conversion section on /apply-for-inclusion.
// Shows 4–6 cards of currently selected members so applicants can answer two
// silent questions on first sight:
//
//   1.  "Am I good enough to be here?"
//   2.  "Do I want to be here?"
//
// Cards are pulled live from the `listings` table (featured + published).
// If the live query returns nothing, we render a quiet "Curation in progress"
// frame that stays truthful, never fake names, never invented venues.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient.js';
import { listingProfileUrl } from '../../../utils/listingUrl.js';
import { navigateTo } from '../../../utils/navigate.js';
import { MEMBERS_FALLBACK, SAMPLE_LISTINGS, COLLECTION_COPY, getPalette } from '../data/inclusionContent.js';
import LuxuryVenueCard from '../../../components/cards/LuxuryVenueCard.jsx';

const GD   = 'var(--font-heading-primary)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

// Map a Supabase listings row to the `v` shape LuxuryVenueCard expects.
// Same component used across the rest of the directory, no custom card.
function shapeForCard(row) {
  // Real listings rows use hero_image/card_image. The hardcoded SAMPLE_LISTINGS
  // dev-fallback (used only if all three live queries return nothing) predates
  // that naming and still uses hero_image_url/cover_image_url, accept both so
  // the rare fallback path isn't left with no image.
  const hero = row.hero_image || row.hero_image_url;
  const card = row.card_image || row.cover_image_url;
  const imgs = [hero, card].filter(Boolean);
  return {
    id:           row.id,
    slug:         row.slug,
    name:         row.name,
    city:         row.city || '',
    region:       row.region || row.region_slug || '',
    country_slug: row.country_slug || '',
    imgs:         imgs.length ? imgs : [card].filter(Boolean),
    priceFrom:    row.price_from || null,
    style:        row.style || row.category || '',
    styles:       Array.isArray(row.styles) ? row.styles : (row.style ? [row.style] : []),
    rating:       row.rating || null,
    reviews:      row.reviews || row.review_count || null,
    capacity_max: row.capacity_max || null,
    description:  row.description || row.short_description || '',
    inclusions:   Array.isArray(row.inclusions) ? row.inclusions : [],
    type:         row.listing_type || 'venue',
  };
}

export default function InsideTheCollection({ darkMode }) {
  const P = getPalette(darkMode);
  const [members, setMembers] = useState(null); // null = loading, [] = empty, [...] = data
  const [usingFallback, setUsingFallback] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      // Column names verified directly against the live `listings` schema,
      // the previous list (cover_image_url/hero_image_url/gallery_images/
      // images/style/inclusions) referenced six columns that don't exist,
      // so this query has always errored and silently fallen back to
      // SAMPLE_LISTINGS below (100% of the time, not intermittently).
      const SELECT = 'id, slug, name, city, region, region_slug, country_slug, category_slug, card_image, hero_image, listing_type, category, price_from, status, is_featured';
      try {
        // Prefer is_featured + published listings (top of the curation tree).
        let { data } = await supabase
          .from('listings')
          .select(SELECT)
          .eq('is_featured', true)
          .eq('status', 'published')
          .limit(3);

        // Fall back to most recently updated published listings.
        if (!data || data.length === 0) {
          const fb = await supabase
            .from('listings')
            .select(SELECT)
            .eq('status', 'published')
            .order('updated_at', { ascending: false })
            .limit(3);
          data = fb.data;
        }

        // If `status` column doesn't exist or RLS blocks it, try a softer
        // query without the filter so the section still populates.
        if (!data || data.length === 0) {
          const open = await supabase
            .from('listings')
            .select(SELECT)
            .order('updated_at', { ascending: false })
            .limit(3);
          data = open.data;
        }

        if (mounted) {
          if (data && data.length > 0) {
            setMembers(data.slice(0, 3));
            setUsingFallback(false);
          } else {
            // Last-resort visual fallback, real LWD venue assets shipped
            // in /public so the section is never empty in development.
            // Honest in code (marked SAMPLE_LISTINGS) but visible on the
            // page so the user can see the layout while the listings
            // table is being populated / flagged is_featured.
            setMembers(SAMPLE_LISTINGS.slice(0, 3));
            setUsingFallback(false);
          }
        }
      } catch {
        if (mounted) {
          setMembers(SAMPLE_LISTINGS.slice(0, 3));
          setUsingFallback(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (members === null) return null; // suppress flash while loading

  return (
    <section style={{
      background:  P.sectionBg,
      padding:     'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
      position:    'relative',
      transition:  'background 0.3s ease',
    }}>
      {/* Eyebrow + title */}
      <div style={{
        textAlign: 'center',
        maxWidth: 720, margin: '0 auto clamp(48px, 6vw, 80px)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
          <span style={{ width: 36, height: 1, background: GOLD }} />
          <span style={{
            fontFamily: NU, fontSize: 9, fontWeight: 600,
            letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD,
          }}>
            {COLLECTION_COPY.eyebrow}
          </span>
          <span style={{ width: 36, height: 1, background: GOLD }} />
        </div>
        <h2 style={{
          fontFamily: GD,
          fontSize:   'clamp(32px, 4.5vw, 56px)',
          fontWeight: 400,
          color:      P.text,
          lineHeight: 1.08,
          letterSpacing: '-0.01em',
          margin:     '0 0 18px',
          transition: 'color 0.3s ease',
        }}>
          {COLLECTION_COPY.title}
        </h2>
        <p style={{
          fontFamily: NU, fontSize: 'clamp(14px, 1.4vw, 17px)',
          color: P.body, lineHeight: 1.7, margin: '0 auto', maxWidth: 660,
          transition: 'color 0.3s ease',
        }}>
          {COLLECTION_COPY.body}
        </p>
      </div>

      {/* Grid, uses the site's standard LuxuryVenueCard so the showcase
          is visually consistent with every other directory page (image
          cycling, hover, badges, all built in). Placeholder tiles only
          appear when no live listings exist (truthful fallback). */}
      <div style={{
        display:             'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(320px, 100%), 1fr))',
        gap:                 'clamp(20px, 2.4vw, 32px)',
        maxWidth:            1280,
        margin:              '0 auto',
      }}>
        {members.map((m, i) => {
          if (m._isPlaceholder) return <PlaceholderCard key={`p-${i}`} m={m} P={P} />;
          const v = shapeForCard(m);
          return (
            <LuxuryVenueCard
              key={m.id || i}
              v={v}
              onView={() => {
                const url = listingProfileUrl(m);
                if (url) navigateTo(url);
              }}
            />
          );
        })}
      </div>

      {/* Footnote when in placeholder mode, visible to keep the page truthful */}
      {usingFallback && (
        <p style={{
          textAlign: 'center', marginTop: 'clamp(36px, 4vw, 48px)',
          fontFamily: NU, fontSize: 11, color: P.subtle,
          fontStyle: 'italic', letterSpacing: '0.04em',
          transition: 'color 0.3s ease',
        }}>
          Curated venue selection in progress. The collection grows by invitation.
        </p>
      )}

      {/* Card-experience footnote, sits below the grid when real cards are
          rendered. Tells the vendor: this is what your listing actually
          does for couples. Italic prose intro + four labeled items. */}
      {!usingFallback && COLLECTION_COPY.footnoteFeatures?.length > 0 && (
        <div style={{
          maxWidth: 1080,
          margin:   'clamp(56px, 6vw, 84px) auto 0',
          padding:  'clamp(28px, 3vw, 40px) clamp(8px, 2vw, 24px)',
          borderTop:    `1px solid ${P.border}`,
          borderBottom: `1px solid ${P.border}`,
          textAlign: 'center',
          transition: 'border-color 0.3s ease',
        }}>
          <p style={{
            fontFamily: GD,
            fontStyle:  'italic',
            fontSize:   'clamp(14px, 1.4vw, 17px)',
            color:      P.body,
            lineHeight: 1.6,
            margin:     '0 auto clamp(24px, 3vw, 32px)',
            maxWidth:   620,
            transition: 'color 0.3s ease',
          }}>
            {COLLECTION_COPY.footnoteIntro}
          </p>
          <ul style={{
            display: 'grid',
            // 5 columns on wide viewports (one per feature). On narrower
            // screens auto-fit collapses to fewer rows. Each cell is wide
            // enough to hold its body line in a single row (whiteSpace:nowrap).
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 'clamp(16px, 2vw, 28px)',
            margin: 0, padding: 0, listStyle: 'none',
            maxWidth: 1200, marginLeft: 'auto', marginRight: 'auto',
          }}>
            {COLLECTION_COPY.footnoteFeatures.map((f, i) => (
              <li key={i} style={{ textAlign: 'center', minWidth: 0 }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  fontFamily: NU, fontSize: 9, fontWeight: 600,
                  letterSpacing: '0.30em', textTransform: 'uppercase',
                  color: GOLD, marginBottom: 6,
                }}>
                  <span aria-hidden style={{
                    width: 4, height: 4, background: GOLD,
                    transform: 'rotate(45deg)',
                  }} />
                  {f.label}
                </div>
                <div style={{
                  fontFamily: NU, fontSize: 11.5, lineHeight: 1.55,
                  color: P.muted,
                  whiteSpace: 'nowrap',
                  transition: 'color 0.3s ease',
                }}>
                  {f.body}
                </div>
              </li>
            ))}
          </ul>

          {/* Showcase explainer, sits between the standard listing features
              above and the closing CTA below. Names Showcase as a real
              product (a bespoke editorial mini-site within LWD), and links
              to a live Showcase example so vendors experience the level. */}
          {COLLECTION_COPY.showcase && (
            <div style={{
              // Black-lux panel, always dark regardless of the page's
              // lights mode. Same vocabulary as the form section: deep
              // editorial black, gold accents, subtle ambient gold wash
              // behind the content. Reads as a premium upgrade callout
              // distinct from the cream Inside the Collection background.
              //
              // Margin-top intentionally generous so the panel reads as a
              // deliberate, separate beat, a tier-up callout, a pause,
              // a moment of consideration, rather than as the next
              // attached element under the features grid.
              maxWidth:     980,
              margin:       'clamp(96px, 12vw, 160px) auto 0',
              padding:      'clamp(40px, 4.5vw, 64px) clamp(28px, 4vw, 56px)',
              background:   '#070604',
              border:       `1px solid ${GOLD}55`,
              textAlign:    'center',
              position:     'relative',
              overflow:     'hidden',
              boxShadow:    '0 18px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
            }}>
              {/* Soft gold ambient wash behind content, adds richness
                  without muddying the type. Sits inside the panel,
                  doesn't pull the eye. */}
              <div aria-hidden style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '120%', height: '180%',
                background: 'radial-gradient(ellipse at center, rgba(201,168,110,0.08) 0%, transparent 65%)',
                pointerEvents: 'none',
              }} />

              <div style={{ position: 'relative' }}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 12,
                  marginBottom: 16,
                  fontFamily: NU, fontSize: 9, fontWeight: 600,
                  letterSpacing: '0.36em', textTransform: 'uppercase',
                  color: GOLD,
                }}>
                  <span aria-hidden style={{
                    width: 4, height: 4, background: GOLD, transform: 'rotate(45deg)',
                  }} />
                  {COLLECTION_COPY.showcase.eyebrow}
                  <span aria-hidden style={{
                    width: 4, height: 4, background: GOLD, transform: 'rotate(45deg)',
                  }} />
                </div>
                <h3 style={{
                  fontFamily:    GD,
                  fontSize:      'clamp(24px, 2.8vw, 36px)',
                  fontWeight:    400,
                  color:         '#f0ebe0',
                  lineHeight:    1.15,
                  letterSpacing: '-0.01em',
                  margin:        '0 0 8px',
                }}>
                  {COLLECTION_COPY.showcase.title}
                </h3>
                {COLLECTION_COPY.showcase.kicker && (
                  <p style={{
                    fontFamily: GD,
                    fontStyle:  'italic',
                    fontSize:   'clamp(14px, 1.4vw, 18px)',
                    fontWeight: 400,
                    color:      GOLD,
                    lineHeight: 1.4,
                    margin:     '0 auto 22px',
                    letterSpacing: '0.01em',
                  }}>
                    {COLLECTION_COPY.showcase.kicker}
                  </p>
                )}
                <p style={{
                  fontFamily: NU, fontSize: 'clamp(13px, 1.3vw, 15px)',
                  lineHeight: 1.7,
                  color: 'rgba(240,235,224,0.78)',
                  margin: '0 auto 26px',
                  maxWidth: 620,
                }}>
                  {COLLECTION_COPY.showcase.body}
                </p>
                {COLLECTION_COPY.showcase.exampleHref && (
                  <a
                    href={COLLECTION_COPY.showcase.exampleHref}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 10,
                      fontFamily:     NU,
                      fontSize:       11,
                      fontWeight:     500,
                      letterSpacing:  '0.28em',
                      textTransform:  'uppercase',
                      color:          GOLD,
                      textDecoration: 'none',
                      padding:        '12px 22px',
                      border:         `1px solid ${GOLD}66`,
                      transition:     'background 0.25s ease, border-color 0.25s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = `${GOLD}14`;
                      e.currentTarget.style.borderColor = GOLD;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.borderColor = `${GOLD}66`;
                    }}
                  >
                    {COLLECTION_COPY.showcase.exampleLabel}
                    <span aria-hidden style={{
                      width: 5, height: 5, background: GOLD, transform: 'rotate(45deg)',
                    }} />
                  </a>
                )}
              </div>
            </div>
          )}

        </div>
      )}
    </section>
  );
}

// ── Placeholder card (region tile, no fake names) ──────────────────────────

function PlaceholderCard({ m, P }) {
  return (
    <div style={{
      background: P.surface,
      border: `1px solid ${P.border}`,
      aspectRatio: '4 / 5',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: 'clamp(24px, 3vw, 40px)',
      textAlign: 'center',
      transition: 'background 0.3s ease, border-color 0.3s ease',
    }}>
      <span aria-hidden style={{
        width: 6, height: 6, background: GOLD,
        transform: 'rotate(45deg)', marginBottom: 24,
      }} />
      <div style={{
        fontFamily: GD, fontSize: 22, color: P.text,
        fontWeight: 400, letterSpacing: '0.02em', marginBottom: 10,
      }}>
        {m.label}
      </div>
      <div style={{
        fontFamily: NU, fontSize: 10, fontWeight: 500,
        letterSpacing: '0.22em', textTransform: 'uppercase',
        color: P.subtle,
      }}>
        {m.note}
      </div>
    </div>
  );
}
